import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  FormHelperText,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { addTags, updateTags } from 'src/services/tag.service';

import { scrollbarStyle } from 'src/sections/style';

function AddUpdateTags({ hideModal, handleFetchTags, row, departmentList }) {
  const { showToast, hideToast } = useToast();
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const [loading, setLoading] = useState(false);
  const { id, departmentId } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'all',
    defaultValues: {
      name: row?.name || '',
      description: row?.description || '',
      department: row?.department?.id || '',
    },
  });

  const onSubmitErrorCallback = (error) => {
    if (error.response?.data === 'name exists') {
      showToast({
        message: t('warningMessages.tagNameExists'),
        severity: 'warning',
        props: { hideToast },
      });
    } else
      showToast({
        message: row
          ? t('errorMessages.errorUpdatingTag')
          : t('errorMessages.errorAddingTag'),
        severity: 'error',
        props: { hideToast },
      });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      data = {
        ...data,
        department: {
          id: departmentList.length > 0 ? data.department : departmentId,
        },
        createdBy: {
          id,
        },
      };
      if (row) {
        await updateTags(row.id, data);
        handleFetchTags();
        showToast({
          message: t('successMessages.tagUpdatedSuccessfully'),
          severity: 'success',
          props: { hideToast },
        });
      } else {
        await addTags(data);
        handleFetchTags();
        showToast({
          message: t('successMessages.tagAddedSuccessfully'),
          severity: 'success',
          props: { hideToast },
        });
      }
      hideModal();
    } catch (error) {
      onSubmitErrorCallback(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const defaultValues = {
      id: row?.id || '',
      name: row?.name || '',
      description: row?.description || '',
      department: row?.department?.id || '',
    };
    reset(defaultValues);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2} mt={1}>
        <TextField
          {...register('name', { required: true })}
          label={t('pages.tagsPage.name')}
          fullWidth
          error={errors.name}
          helperText={errors.name && t('formControl.fieldRequired')}
          size={isSmallScreen ? 'small' : 'medium'}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: isSmallScreen ? '0.7rem' : '1rem',
            },
            '& .MuiFormLabel-root': {
              fontSize: isSmallScreen ? '0.7rem' : '1rem',
            },
          }}
        />

        <TextField
          multiline
          minRows={4}
          name="description"
          {...register('description', {})}
          label={t('pages.tagsPage.description')}
          error={errors.description}
          helperText={errors.description && t('formControl.fieldRequired')}
          size={isSmallScreen ? 'small' : 'medium'}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: isSmallScreen ? '0.7rem' : '1rem',
            },
            '& .MuiFormLabel-root': {
              fontSize: isSmallScreen ? '0.7rem' : '1rem',
            },
          }}
        />

        {departmentList.length > 0 && (
          <FormControl
            fullWidth
            error={!!errors.department}
            data-testid="user-department-select-control"
            size={isSmallScreen ? 'small' : 'medium'}
            disabled={!!row}
          >
            <InputLabel id="department-select-label">
              <Typography
                variant="body2"
                sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
              >
                {t('pages.tagsPage.department')}
              </Typography>
            </InputLabel>

            <Select
              labelId="department-select-label"
              id="department-select"
              inputProps={{
                'data-testid': 'department-select',
              }}
              value={watch('department')}
              name="department"
              label={
                <Typography
                  variant="body2"
                  sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
                >
                  {t('pages.tagsPage.department')}
                </Typography>
              }
              defaultValue={row?.department?.id || -1}
              {...register('department', {
                required: t('formControl.selectDepartment'),
                onChange: (e) => {
                  setValue('department', e.target.value, { shouldValidate: true });
                },
              })}
              MenuProps={{
                PaperProps: {
                  sx: {
                    ...scrollbarStyle,
                  },
                },
              }}
            >
              {departmentList?.length > 0 ? (
                departmentList.map((department) => {
                  const { id, name } = department;
                  return (
                    <MenuItem key={id} value={department?.id}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
                      >
                        {name}
                      </Typography>
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled value="">
                  <Typography
                    variant="body2"
                    sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
                  >
                    {t('infoMessages.noOptions')}
                  </Typography>
                </MenuItem>
              )}
            </Select>
            <FormHelperText sx={{ fontSize: isSmallScreen ? '0.6rem' : '0.8rem' }}>
              {errors.department?.message}
            </FormHelperText>
          </FormControl>
        )}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 2 }}
        >
          {row && (
            <LoadingButton
              sx={{
                backgroundColor: '#d21426',
                color: '#fff',
                fontSize: isSmallScreen ? '0.65rem' : '1rem',
              }}
              fullWidth
              size={isSmallScreen ? 'small' : 'medium'}
              variant="contained"
              color="error"
              disabled={!isDirty}
              onClick={handleReset}
            >
              {t('buttons.cancelChanges')}
            </LoadingButton>
          )}
          <LoadingButton
            sx={{
              backgroundColor: 'success.dark',
              color: '#fff',
              ':hover': { backgroundColor: 'success.darker' },
              fontSize: isSmallScreen ? '0.65rem' : '1rem',
            }}
            fullWidth
            size={isSmallScreen ? 'small' : 'medium'}
            type="submit"
            variant="contained"
            disabled={!isValid || !isDirty}
            loading={loading}
          >
            {row ? t('buttons.editTag') : t('buttons.addTag')}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
}

AddUpdateTags.propTypes = {
  hideModal: PropTypes.func,
  handleFetchTags: PropTypes.func,
  row: PropTypes.object,
  departmentList: PropTypes.array,
};

export default AddUpdateTags;
